import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ClockIcon, ExternalLinkIcon, MapPinIcon, MessageSquareIcon, NavigationIcon, PhoneIcon } from 'lucide-react';
import { LOCATIONS, SERVICE_MENUS } from '../data/locations';
import { THERAPISTS } from '../data/therapists';
function toIntlNumber(phone: string) {
  const digits = phone.replace(/\D/g, '');
  return digits.startsWith('0') ? `+61${digits.slice(1)}` : digits;
}
export function LocationDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const location = LOCATIONS.find(l => l.slug === slug);
  if (!location) {
    return <Navigate to="/locations" replace data-id="element-286" data-mp="24xazsx57080l" />;
  }
  const mapQuery = encodeURIComponent(`${location.name}, ${location.address}`);
  const smsBody = `Hi ${location.name}, I'd like to book a massage. Sent via the Me Time website.`;
  const others = LOCATIONS.filter(l => l.slug !== location.slug).slice(0, 3);
  const studioTherapists = THERAPISTS.filter(t => t.locationSlug === location.slug);
  return <main className="w-full bg-ink-950" data-id="element-287" data-mp="2i33uipyacpy1">
      <section className="relative w-full overflow-hidden" data-id="element-288" data-mp="2i3ww7cie0p9b">
        <img src={location.image} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" data-id="element-289" data-mp="243m2m1pp0wkg" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-950/85 via-ink-950/60 to-ink-950" data-id="element-290" data-mp="294jked6sjolf" />
        <div className="absolute inset-0 mix-blend-soft-light" style={{
        backgroundImage: 'radial-gradient(60% 60% at 18% 20%, rgba(232,54,127,0.5) 0%, rgba(232,54,127,0) 65%), radial-gradient(60% 60% at 82% 30%, rgba(168,85,247,0.45) 0%, rgba(168,85,247,0) 65%)'
      }} data-id="element-291" data-mp="2wu4ffug7ycb2" />
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} className="relative mx-auto w-full max-w-7xl px-5 py-20 sm:px-8 sm:py-24" data-id="element-292" data-mp="3fr5ktgdoqefj">
          <Link to="/locations" className="inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-lotus-500" data-id="element-293" data-mp="3uct8hbj3k135">
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-294" data-mp="3keltgy8dbt3s" />
            All locations
          </Link>
          <p className="mt-8 text-xs font-semibold uppercase tracking-[0.28em] text-lotus-500" data-id="element-295" data-mp="3ffacpp804nm7">
            {location.suburb}
          </p>
          <h1 className="mt-3 text-4xl font-extrabold tracking-tight text-white sm:text-5xl" data-id="element-296" data-mp="3bgtfbxedurhz">
            {location.name}
          </h1>
          <p className="mt-4 flex items-center gap-2 text-neutral-300" data-id="element-297" data-mp="31ody52awu6y5">
            <MapPinIcon className="h-5 w-5 text-lotus-500" aria-hidden="true" data-id="element-298" data-mp="31mkr3lwdxkpm" />
            {location.address}
          </p>
        </motion.div>
      </section>

      <section aria-label="Venue details" className="glow-warm w-full bg-ink-950" data-id="element-299" data-mp="2rpg2lff2hd5n">
        <div className="mx-auto grid w-full max-w-7xl gap-10 px-5 py-16 sm:px-8 lg:grid-cols-[1fr_360px]" data-id="element-300" data-mp="2nq77o78aj98i">
          <div className="space-y-10" data-id="element-301" data-mp="136brmid0do5s">
            <div className="overflow-hidden rounded-2xl border border-white/5" data-id="element-302" data-mp="139g380sszm6h">
              <iframe title={`Map showing ${location.name}`} src={`https://maps.google.com/maps?q=${mapQuery}&z=15&output=embed`} loading="lazy" className="h-[320px] w-full border-0 grayscale-[0.4]" referrerPolicy="no-referrer-when-downgrade" data-id="element-303" data-mp="176vrhtoyv5vm" />
            </div>

            <div data-id="element-304" data-mp="ohfp10tq13ck">
              <h2 className="text-2xl font-bold tracking-tight text-white" data-id="element-305" data-mp="oiz62tn7bife">
                Treatments at {location.suburb}
              </h2>
              <div className="mt-6 grid gap-5 sm:grid-cols-2" data-id="element-306" data-mp="tfh8vnyz16p3">
                {SERVICE_MENUS.map(menu => <section key={menu.name} aria-label={menu.name} className="rounded-2xl border border-white/5 bg-ink-850 p-6" data-id="element-307" data-mp="sweeen78ehb">
                    <h3 className="text-lg font-bold text-white" data-id="element-308" data-mp="27gdk7i3qvv0u">{menu.name}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-neutral-400" data-id="element-309" data-mp="23fa6b159c929">
                      {menu.description}
                    </p>
                    <ul className="mt-5 divide-y divide-white/5" data-id="element-310" data-mp="2h7glol0l6c0n">
                      {menu.tiers.map(tier => <li key={tier.duration} className="flex items-baseline justify-between gap-4 py-3" data-id="element-311" data-mp="2ceng5uxamzni">
                          <span className="text-sm text-neutral-300" data-id="element-312" data-mp="2m501hpcxmwup">{tier.duration}</span>
                          <span className="font-display text-2xl font-light text-lotus-400" data-id="element-313" data-mp="3049ekg5udaug">
                            {tier.price}
                          </span>
                        </li>)}
                    </ul>
                  </section>)}
              </div>
            </div>

            {studioTherapists.length > 0 && <div data-id="element-314" data-mp="3cfa6bwnco8yk">
                <h2 className="font-display text-3xl font-light tracking-wide text-white" data-id="element-315" data-mp="3cgtmln90ak0c">
                  Ladies at this location
                </h2>
                <div className="mt-6 grid gap-4 sm:grid-cols-3" data-id="element-316" data-mp="33ngl3wwvma2n">
                  {studioTherapists.map(t => <Link key={t.slug} to={`/therapists/${t.slug}`} className="group overflow-hidden rounded-2xl border border-white/5 bg-ink-850 transition-colors hover:border-lotus-500/40" data-id="element-317" data-mp="37otp01ges36x">
                      <img src={t.image} alt={`${t.name}, massage therapist`} className="h-56 w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" data-id="element-318" data-mp="2kom56klof3kw" />
                      <span className="block p-4" data-id="element-319" data-mp="myujrzqr0880">
                        <span className="block font-display text-xl font-light tracking-wide text-white" data-id="element-320" data-mp="myb3engqxrw3">
                          {t.name}
                        </span>
                        <span className="mt-1 block text-[10px] font-medium uppercase tracking-[0.24em] text-lotus-500" data-id="element-321" data-mp="1qwf3j6n15b38">
                          {t.origin} · {t.experience}
                        </span>
                      </span>
                    </Link>)}
                </div>
              </div>}

            <div data-id="element-322" data-mp="2elr81t7ewuz7">
              <h2 className="font-display text-3xl font-light tracking-wide text-white" data-id="element-323" data-mp="2eodl28d2o2ot">
                Other locations nearby
              </h2>
              <div className="mt-6 grid gap-4 sm:grid-cols-3" data-id="element-324" data-mp="25t5tkpatdag6">
                {others.map(other => <Link key={other.slug} to={`/locations/${other.slug}`} className="rounded-xl border border-white/5 bg-ink-850 p-5 transition-colors hover:border-lotus-500/50" data-id="element-325" data-mp="20tta8ej5ilic">
                    <span className="block text-xs font-semibold uppercase tracking-[0.2em] text-lotus-500" data-id="element-326" data-mp="30uobq3poms2h">
                      {other.suburb}
                    </span>
                    <span className="mt-2 block font-semibold text-white" data-id="element-327" data-mp="35u18bg6cvdi9">{other.name}</span>
                  </Link>)}
              </div>
            </div>
          </div>

          <aside className="lg:sticky lg:top-28 lg:self-start" data-id="element-328" data-mp="3ew0oy7dgkfko">
            <div className="rounded-2xl border border-white/5 bg-ink-900 p-6" data-id="element-329" data-mp="3juc0cmjeiy50">
              <h2 className="text-lg font-bold text-white" data-id="element-330" data-mp="3osdi9453crew">Book at this venue</h2>
              <ul className="mt-5 space-y-3.5 text-sm text-neutral-300" data-id="element-331" data-mp="3oqaktz1lzw05">
                <li className="flex items-start gap-3" data-id="element-332" data-mp="3tk5bxnjjnd99">
                  <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-lotus-500" aria-hidden="true" data-id="element-333" data-mp="2dkglrmghj6f" />
                  {location.address}
                </li>
                <li className="flex items-start gap-3" data-id="element-334" data-mp="besalxhxvxup">
                  <PhoneIcon className="mt-0.5 h-4 w-4 shrink-0 text-lotus-500" aria-hidden="true" data-id="element-335" data-mp="wop54nihq5ar" />
                  <a href={`tel:${location.phone.replace(/\s/g, '')}`} className="hover:text-lotus-500" data-id="element-336" data-mp="rp2rwe2nwp1y">
                    {location.phone}
                  </a>
                </li>
                <li className="flex items-start gap-3" data-id="element-337" data-mp="dy7jt3zzmbzf">
                  <ClockIcon className="mt-0.5 h-4 w-4 shrink-0 text-lotus-500" aria-hidden="true" data-id="element-338" data-mp="dypjkaluz8c3" />
                  {location.hours}
                </li>
              </ul>

              <div className="mt-6 space-y-3" data-id="element-339" data-mp="ixanak0bka5a">
                {location.squareBookingUrl ? <a href={location.squareBookingUrl} target="_blank" rel="noopener noreferrer" className="glow-pink inline-flex w-full items-center justify-center gap-2 rounded-full bg-lotus-500 px-6 py-3.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white transition-colors hover:bg-lotus-600" data-id="element-340" data-mp="ivg3xa4p4q3b">
                    Choose a time
                    <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" data-id="element-341" data-mp="1ryu2t3w8uh2b" />
                  </a> : <a href={`sms:${toIntlNumber(location.phone)}?&body=${encodeURIComponent(smsBody)}`} className="glow-pink inline-flex w-full items-center justify-center gap-2 rounded-full bg-lotus-500 px-6 py-3.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white transition-colors hover:bg-lotus-600" data-id="element-342" data-mp="1wxf8hiu9lsy5">
                    <MessageSquareIcon className="h-4 w-4" aria-hidden="true" data-id="element-343" data-mp="2fp7yd4e1ey1n" />
                    Text to book
                  </a>}
                <a href={`tel:${location.phone.replace(/\s/g, '')}`} className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-orchid-400/50 px-6 py-3.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-orchid-300 transition-colors hover:bg-orchid-500/15 hover:text-white" data-id="element-344" data-mp="1499hwfgj59qe">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-345" data-mp="1s4f7pd107mic" />
                  Call venue
                </a>
                <a href={`https://maps.google.com/maps?q=${mapQuery}`} target="_blank" rel="noopener noreferrer" className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/20 px-6 py-3.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-200 transition-colors hover:border-lotus-500/60 hover:text-lotus-300" data-id="element-346" data-mp="1e4mi5l17rcby">
                  <NavigationIcon className="h-4 w-4" aria-hidden="true" data-id="element-347" data-mp="2zg4k02fnsgxp" />
                  Get directions
                </a>
              </div>

              <p className="mt-5 text-xs leading-relaxed text-neutral-500" data-id="element-348" data-mp="rvpaz0jqix3x">
                Walk-ins welcome when a therapist is free. Services for men only, ages 18+.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </main>;
}